import { appAlert } from '@/lib/appAlert';
import { Ionicons } from '@expo/vector-icons';
import { useFocusEffect, useLocalSearchParams, useRouter } from 'expo-router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActivityIndicator,
  Image,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { fontSize, radius, spacing } from '@/constants/theme';
import type { AppColors } from '@/constants/theme';
import { useTheme } from '@/contexts/ThemeContext';
import { useProfile } from '@/contexts/ProfileContext';
import { fetchProfilesByIds } from '@/lib/agreements';
import {
  deleteFeatureRequest,
  fetchFeatureRequests,
  getFeedbackScreenshotUrl,
  setFeatureRequestStatus,
  type FeatureRequestRow,
  type FeatureRequestStatus,
} from '@/lib/featureRequests';
import type { Profile } from '@/lib/database.types';

type FilterKey = 'all' | FeatureRequestStatus;

export default function AdminFeedbackScreen() {
  const { t } = useTranslation();
  const { profile } = useProfile();
  const router = useRouter();
  const { id: highlightId } = useLocalSearchParams<{ id?: string }>();
  const insets = useSafeAreaInsets();
  const { colors } = useTheme();
  const styles = useMemo(() => makeStyles(colors), [colors]);

  const isAdmin = !!profile?.is_admin;

  const [rows, setRows] = useState<FeatureRequestRow[]>([]);
  const [profiles, setProfiles] = useState<Record<string, Profile>>({});
  const [loading, setLoading] = useState(true);
  // Αν έρχεται από notification για συγκεκριμένο report, δείξε τα πάντα ώστε
  // να το βρει ανεξαρτήτως κατάστασης (μπορεί ήδη να το είδε άλλος admin).
  const [filter, setFilter] = useState<FilterKey>(highlightId ? 'all' : 'new');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [screenshotUrls, setScreenshotUrls] = useState<Record<string, string>>({});
  const [busyId, setBusyId] = useState<string | null>(null);
  const didAutoOpen = useRef(false);

  const load = useCallback(async () => {
    if (!isAdmin) return;
    setLoading(true);
    try {
      const data = await fetchFeatureRequests(filter === 'all' ? null : filter);
      setRows(data);
      setProfiles(await fetchProfilesByIds(data.map((r) => r.user_id)));
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [filter, isAdmin]);

  useFocusEffect(useCallback(() => { void load(); }, [load]));

  useEffect(() => {
    if (!highlightId || didAutoOpen.current || rows.length === 0) return;
    const target = rows.find((r) => r.id === highlightId);
    if (target) {
      didAutoOpen.current = true;
      void onExpand(target);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, highlightId]);

  if (!isAdmin) {
    return (
      <View style={[styles.center, { paddingBottom: insets.bottom }]}>
        <Text style={styles.errorText}>{t('adminScreen.noAccessScreen')}</Text>
      </View>
    );
  }

  async function onExpand(row: FeatureRequestRow) {
    const opening = expandedId !== row.id;
    setExpandedId(opening ? row.id : null);
    if (opening) {
      if (row.screenshot_path && !screenshotUrls[row.id]) {
        const url = await getFeedbackScreenshotUrl(row.screenshot_path).catch(() => null);
        if (url) setScreenshotUrls((prev) => ({ ...prev, [row.id]: url }));
      }
      if (row.status === 'new') {
        await setFeatureRequestStatus(row.id, 'read').catch(() => {});
        setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, status: 'read' } : r)));
      }
    }
  }

  async function onMarkDone(row: FeatureRequestRow) {
    setBusyId(row.id);
    try {
      await deleteFeatureRequest(row.id, row.screenshot_path);
      setRows((prev) => prev.filter((r) => r.id !== row.id));
    } catch {
      appAlert(t('common.error'), t('adminScreen.feedbackActionFailed'));
    } finally {
      setBusyId(null);
    }
  }

  function onDelete(row: FeatureRequestRow) {
    appAlert(
      t('adminScreen.feedbackDeleteTitle'),
      t('adminScreen.feedbackDeleteBody'),
      [
        { text: t('common.cancel'), style: 'cancel' },
        {
          text: t('common.delete'),
          style: 'destructive',
          onPress: async () => {
            setBusyId(row.id);
            try {
              await deleteFeatureRequest(row.id, row.screenshot_path);
              setRows((prev) => prev.filter((r) => r.id !== row.id));
            } catch {
              appAlert(t('common.error'), t('adminScreen.feedbackActionFailed'));
            } finally {
              setBusyId(null);
            }
          },
        },
      ],
    );
  }

  const FILTERS: { key: FilterKey; label: string }[] = [
    { key: 'new', label: t('adminScreen.feedbackFilter_new') },
    { key: 'read', label: t('adminScreen.feedbackFilter_read') },
    { key: 'all', label: t('adminScreen.feedbackFilter_all') },
  ];

  return (
    <View style={styles.flex}>
      <View style={[styles.topBar, { paddingTop: insets.top + spacing.md }]}>
        <Pressable onPress={() => router.back()} hitSlop={12} accessibilityRole="button">
          <Text style={styles.back}>{t('common.back')}</Text>
        </Pressable>
        <Text style={styles.title}>{t('adminScreen.feedbackTitle')}</Text>
        <View style={styles.spacer} />
      </View>

      <View style={styles.tabs}>
        {FILTERS.map((f) => (
          <Pressable
            key={f.key}
            style={[styles.tab, filter === f.key && styles.tabActive]}
            onPress={() => setFilter(f.key)}
            accessibilityRole="tab"
          >
            <Text style={[styles.tabText, filter === f.key && styles.tabTextActive]}>{f.label}</Text>
          </Pressable>
        ))}
      </View>

      {loading ? (
        <View style={styles.center}>
          <ActivityIndicator size="large" color={colors.primary} />
        </View>
      ) : rows.length === 0 ? (
        <View style={styles.center}>
          <Ionicons name="chatbox-ellipses-outline" size={40} color={colors.border} />
          <Text style={styles.emptyText}>{t('adminScreen.feedbackEmpty')}</Text>
        </View>
      ) : (
        <ScrollView contentContainerStyle={[styles.list, { paddingBottom: insets.bottom + spacing.xl }]}>
          {rows.map((row) => {
            const p = profiles[row.user_id];
            const name = p?.full_name?.trim() || p?.contact_email?.trim() || t('adminScreen.feedbackUnknownUser');
            const expanded = expandedId === row.id;
            const busy = busyId === row.id;
            return (
              <Pressable key={row.id} style={styles.card} onPress={() => onExpand(row)}>
                <View style={styles.cardTop}>
                  <View style={styles.cardTopLeft}>
                    {row.status === 'new' && <View style={styles.newDot} />}
                    <Text style={styles.cardUser} numberOfLines={1}>{name}</Text>
                  </View>
                  <Text style={styles.cardDate}>{new Date(row.created_at).toLocaleDateString('el-GR')}</Text>
                </View>
                <Text style={styles.cardMessage} numberOfLines={expanded ? undefined : 3}>
                  {row.message}
                </Text>
                {!!row.screenshot_path && !expanded && (
                  <View style={styles.screenshotHint}>
                    <Ionicons name="image-outline" size={14} color={colors.textMuted} />
                    <Text style={styles.screenshotHintText}>{t('adminScreen.feedbackHasScreenshot')}</Text>
                  </View>
                )}
                {expanded && (
                  <>
                    {!!row.screenshot_path && screenshotUrls[row.id] && (
                      <Image source={{ uri: screenshotUrls[row.id] }} style={styles.screenshot} resizeMode="contain" />
                    )}
                    <View style={styles.actionRow}>
                      {row.status !== 'done' && (
                        <Pressable style={[styles.actionBtn, styles.actionBtnPrimary]} onPress={() => onMarkDone(row)} disabled={busy}>
                          <Text style={styles.actionBtnPrimaryText}>{busy ? '…' : t('adminScreen.feedbackMarkDone')}</Text>
                        </Pressable>
                      )}
                      <Pressable style={styles.actionBtn} onPress={() => onDelete(row)} disabled={busy}>
                        <Text style={styles.actionBtnText}>{t('common.delete')}</Text>
                      </Pressable>
                    </View>
                  </>
                )}
              </Pressable>
            );
          })}
        </ScrollView>
      )}
    </View>
  );
}

function makeStyles(colors: AppColors) {
  return StyleSheet.create({
    flex: { flex: 1, backgroundColor: colors.background },
    center: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      gap: spacing.md,
      paddingHorizontal: spacing.xl,
    },
    errorText: { fontSize: fontSize.md, color: colors.danger, textAlign: 'center' },
    emptyText: { fontSize: fontSize.md, color: colors.textMuted, textAlign: 'center' },
    topBar: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: spacing.lg,
      paddingBottom: spacing.md,
    },
    back: { fontSize: fontSize.md, fontWeight: '600', color: colors.primary },
    title: { color: colors.text, fontSize: fontSize.lg, fontWeight: '700' },
    spacer: { width: 44 },
    tabs: {
      flexDirection: 'row',
      marginHorizontal: spacing.lg,
      borderRadius: radius.md,
      borderWidth: 1,
      borderColor: colors.border,
      overflow: 'hidden',
      marginBottom: spacing.sm,
    },
    tab: { flex: 1, paddingVertical: 10, alignItems: 'center' },
    tabActive: { backgroundColor: colors.primary },
    tabText: { color: colors.textMuted, fontSize: fontSize.sm, fontWeight: '600' },
    tabTextActive: { color: colors.textOnPrimary },
    list: { paddingHorizontal: spacing.lg, gap: spacing.sm },
    card: {
      backgroundColor: colors.surface,
      borderRadius: radius.lg,
      borderWidth: 1,
      borderColor: colors.border,
      padding: spacing.md,
      gap: spacing.xs,
    },
    cardTop: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
    cardTopLeft: { flexDirection: 'row', alignItems: 'center', gap: 6, flex: 1 },
    newDot: { width: 8, height: 8, borderRadius: 4, backgroundColor: colors.primary },
    cardUser: { color: colors.text, fontSize: fontSize.sm, fontWeight: '700', flexShrink: 1 },
    cardDate: { color: colors.textMuted, fontSize: fontSize.xs },
    cardMessage: { color: colors.text, fontSize: fontSize.sm, lineHeight: 20 },
    screenshotHint: { flexDirection: 'row', alignItems: 'center', gap: 4 },
    screenshotHintText: { color: colors.textMuted, fontSize: fontSize.xs },
    screenshot: { width: '100%', height: 260, borderRadius: radius.md, marginTop: spacing.xs, backgroundColor: colors.surfaceAlt },
    actionRow: { flexDirection: 'row', gap: spacing.sm, marginTop: spacing.xs },
    actionBtn: {
      flex: 1,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: radius.md,
      paddingVertical: 10,
      alignItems: 'center',
    },
    actionBtnPrimary: { backgroundColor: colors.primary, borderColor: colors.primary },
    actionBtnPrimaryText: { color: colors.textOnPrimary, fontSize: fontSize.sm, fontWeight: '700' },
    actionBtnText: { color: colors.danger, fontSize: fontSize.sm, fontWeight: '700' },
  });
}
